<template>
    <div class="priceAlertsTable">
        <div class="table-header">
            <strong>{{ section.label }}</strong>
            <div class="ml-auto print:hidden">
                <a @click="emitAlertAction(section.name, 'add')" class="btn btn-xs btn-yellow"><i class="fas fa-plus"></i>&nbsp;Add {{ section.label }} Alert</a>
            </div>
        </div>
        <div class="data-table">
            <div class="table-responsive border mb-1" @scroll="handleTableScroll($event)">
                <div class="table-scroller">
                    <table ref="containerRef" class="table relative table-striped table-hover price-alerts-table">
                        <thead ref="theadRef">
                            <tr>
                                <th @click="sortTable('name', section.name)" class="cursor-pointer">
                                    Name
                                    <i v-if="section.sortField === 'name'" class="fas ml-1" :class="{ 'fa-sort-alpha-down': section.sortDir === 'asc', 'fa-sort-alpha-up': section.sortDir === 'desc' }" aria-hidden="true"></i>
                                    <i v-else class="fas fa-sort text-gray-400 ml-1'" aria-hidden="true"></i>
                                </th>
                                <th class="print:hidden"></th>
                                <th class="text-center">Last Price</th>
                                <th class="text-center">Change</th>
                                <th class="text-center">% Change</th>
                                <th class="text-center cursor-pointer" @click="sortTable('above', section.name)">
                                    Price Above
                                    <i v-if="section.sortField === 'above'" class="fas ml-1" :class="{ 'fa-sort-numeric-down': section.sortDir === 'asc', 'fa-sort-numeric-up': section.sortDir === 'desc' }" aria-hidden="true"></i>
                                    <i v-else class="fas fa-sort text-gray-400 ml-1'" aria-hidden="true"></i>
                                </th>
                                <th class="text-center cursor-pointer" @click="sortTable('below', section.name)">
                                    Price Below
                                    <i v-if="section.sortField === 'below'" class="fas ml-1" :class="{ 'fa-sort-numeric-down': section.sortDir === 'asc', 'fa-sort-numeric-up': section.sortDir === 'desc' }" aria-hidden="true"></i>
                                    <i v-else class="fas fa-sort text-gray-400 ml-1'" aria-hidden="true"></i>
                                </th>
                                <th class="text-center print:hidden">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-for="(alertItem, symbol) in priceAlerts" :key="alertItem[0].name">
                                <tr>
                                    <td class="price-alerts-name">
                                        {{ alertItem[0].name }}
                                    </td>
                                    <td class="text-center print:hidden">
                                        <div class="inline-block" v-if="buttons[section.name]">
                                            <button-group :symbol="symbol" :groups="buttons[section.name]">
                                            </button-group>
                                        </div>
                                    </td>
                                    <td class="text-center">{{ getQuoteDataField(alertItem[0], 'lastPrice') }}</td>
                                    <td class="text-center" :class="getQuoteClass(alertItem[0], 'priceChange')">{{ getQuoteDataField(alertItem[0], 'priceChange') }}</td>
                                    <td class="text-center" :class="getQuoteClass(alertItem[0], 'percentChange')">{{ getQuoteDataField(alertItem[0], 'percentChange') }}</td>
                                    <td class="text-center">
                                        <span :class="{ 'font-bold text-blue-500': !getLastTriggered(alertItem, 'above') && getAlertState(alertItem, 'above') == 'Active', 'font-bold': getAlertState(alertItem, 'above') == 'Triggered' }">
                                            {{ getLastPrice(alertItem, 'above') }}
                                        </span>
                                        <span :class="{ 'hidden': !getLastTriggered(alertItem, 'above'), 'ml-1 italic': getLastTriggered(alertItem, 'above') }">
                                            {{ getLastTriggered(alertItem, 'above') }}
                                        </span>
                                    </td>
                                    <td class="text-center">
                                        <span :class="{ 'text-blue-500 font-bold': !getLastTriggered(alertItem, 'below') && getAlertState(alertItem, 'below') == 'Active', 'font-bold': getAlertState(alertItem, 'below') == 'Triggered' }">
                                            {{ getLastPrice(alertItem, 'below') }}
                                        </span>
                                        <span :class="{ 'hidden': !getLastTriggered(alertItem, 'below'), 'ml-1 italic': getLastTriggered(alertItem, 'below') }">
                                            {{ getLastTriggered(alertItem, 'below') }}
                                        </span>
                                    </td>
                                    <td nowrap class="text-center print:hidden">
                                        <span @click="emitAlertAction(alertItem, 'edit')" v-tooltip="{ content: 'Edit Alert', theme: 'info' }" class="btn btn-shadow-none btn-blue-ghost btn-xs"><i class="far fa-edit"></i></span>
                                        <span @click="removeAlertData = alertItem" v-tooltip="{ content: 'Delete Alert', theme: 'red' }" class="btn btn-shadow-none btn-red-ghost btn-xs"><i class="fas fa-times"></i></span>
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                        <tfoot>
                            <tr>
                                <th>Name</th>
                                <th class="print:hidden"></th>
                                <th class="text-center">Last Price</th>
                                <th class="text-center">Change</th>
                                <th class="text-center">% Change</th>
                                <th class="text-center">Price Above</th>
                                <th class="text-center">Price Below</th>
                                <th class="text-center print:hidden">Actions</th>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </div>

        <modal @close="removeAlertData = null" v-if="removeAlertData" className="max-w-sm w-full">
            <template #header>
                <div class="text-red-500">
                    <i class="fas fa-exclamation-circle mr-1"></i>
                    Delete Alert
                </div>
            </template>
            <template #body>
                <div class="mb-3 pt-4 pb-2">
                    <p>Are you sure you want to delete alert record</p>
                    <p><strong>{{ removeAlertData[0].name }}</strong>?</p>
                    <p class="mt-2">Deleting this alert will deactivate any active alerts and erase any history of triggered alerts.</p>
                </div>
            </template>

            <template #footer>
                <div class="flex justify-center">
                    <div class="btn btn-sm btn-red-ghost inline-flex mx-1" @click="removeAlertData = null">Cancel</div>
                    <div class="btn btn-sm btn-red inline-flex mx-1" @click="emitAlertAction(removeAlertData, 'delete')">Delete</div>
                </div>
            </template>
        </modal>
    </div>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue';
import { useStickyHeader } from '../../DataTable/mixins/StickTableHeader';

const props = defineProps({
    initPriceAlertData: Object,
    section: Object,
    buttons: Object,
});

const { containerRef, theadRef, handleScroll, resizeHeader } = useStickyHeader();

const priceAlerts = ref(null);
const symbolQuotes = ref(props.initPriceAlertData.quotes);
const removeAlertData = ref(null);

const emit = defineEmits(['onAlertSort', 'onAlertState', 'onAlertAction']);

// Methods
const sortTable = (field) => {
    if (props.section.sortField === field) {
        props.section.sortDir = props.section.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
        props.section.sortField = field;
    }
    emit('onAlertSort', props.section);
};

const getClass = (value) => {
    if (value > 0) {
        return 'text-up';
    } else if (value < 0) {
        return 'text-down';
    } else if (value === 0) {
        return 'text-unch';
    }
};

const getQuoteClass = (alertItem, fieldName) => {
    if (symbolQuotes.value) {
        let fieldData = symbolQuotes.value.find(quote => quote.symbol === alertItem.conditions[0].property.target.identifier);
        if (fieldData && fieldData['raw'][fieldName]) {
            return getClass(fieldData['raw'][fieldName]);
        }
    }
    return '';
};

const getAlertState = (alertItems, key) => {
    let operator_id = key === 'above' ? 2 : 3;
    let alertItem = alertItems.find(a => a.conditions[0].operator.operator_id === operator_id);
    return alertItem ? alertItem.alert_state : null;
};

const getLastTriggered = (alertItems, key) => {
    let operator_id = key === 'above' ? 2 : 3;
    let alertItem = alertItems.find(a => a.conditions[0].operator.operator_id === operator_id);
    return alertItem && alertItem.last_trigger_date ? toDateString(alertItem.last_trigger_date) : null;
};

const getLastPrice = (alertItems, key) => {
    let operator_id = key === 'above' ? 2 : 3;
    let alertItem = alertItems.find(a => a.conditions[0].operator.operator_id === operator_id);
    return alertItem ? (alertItem.notes.lastPriceFormatted || alertItem.conditions[0].operator.operand_display) : '-';
};

const toDateString = (value) => {
    return value ? new Date(parseInt(value)).toLocaleDateString('en-US', { hour: 'numeric', minute: 'numeric', hour12: false }) + ' CT' : '-';
};

const getQuoteDataField = (alertItem, fieldName) => {
    if (symbolQuotes.value) {
        let fieldData = symbolQuotes.value.find(quote => quote.symbol === alertItem.conditions[0].property.target.identifier);
        return fieldData && fieldData[fieldName] ? fieldData[fieldName] : '-';
    }
    return '-';
};

// const emitAlertState = (item, state) => {
//     emit('onAlertState', item, state);
// };

const emitAlertAction = (alertItem, action) => {
    if (action === 'delete') {
        removeAlertData.value = null;
    }
    emit('onAlertAction', alertItem, action);
};

const sortAlerts = (alerts) => {
    let sortedObj = {};
    let keys = Object.keys(alerts);

    if (props.section.sortField === 'name') {
        keys.sort((a, b) => {
            let af = alerts[a][0][props.section.sortField];
            let bf = alerts[b][0][props.section.sortField];
            if (af < bf) {
                return props.section.sortDir === 'asc' ? -1 : 1;
            }
            if (af > bf) {
                return props.section.sortDir === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }

    if (props.section.sortField === 'above' || props.section.sortField === 'below') {
        keys.sort((a, b) => {
            let aFieldData = alerts[a].find(a => a.notes.priceType === props.section.sortField);
            let af = aFieldData ? aFieldData.notes.lastPrice : '';
            let bFieldData = alerts[b].find(b => b.notes.priceType === props.section.sortField);
            let bf = bFieldData ? bFieldData.notes.lastPrice : '';
            if (af < bf) {
                return props.section.sortDir === 'asc' ? -1 : 1;
            }
            if (af > bf) {
                return props.section.sortDir === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }

    keys.forEach(key => {
        sortedObj[key] = alerts[key];
    });

    return sortedObj;
};

const filterAlerts = (alerts) => {
    let filteredAlerts = {};

    if (Object.keys(alerts).length > 0) {
        Object.keys(alerts).forEach(key => {
            if (alerts[key][0].notes && alerts[key][0].notes.section === props.section.name) {
                filteredAlerts[key] = alerts[key];
            }
        });
    }

    return sortAlerts(filteredAlerts);
};

// Watchers
watch(() => props.section, (section) => {
    priceAlerts.value = sortAlerts(priceAlerts.value);
}, { deep: true });

watch(() => props.initPriceAlertData.alerts, (priceAlertsData) => {
    priceAlerts.value = filterAlerts(priceAlertsData);
}, { deep: true });

watch(() => props.initPriceAlertData.quotes, (symbolQuotesData) => {
    symbolQuotes.value = symbolQuotesData;
}, { deep: true });

// Lifecycle hooks
onMounted(() => {
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', resizeHeader);

    if (props.initPriceAlertData && Object.keys(props.initPriceAlertData.alerts).length > 0) {
        priceAlerts.value = filterAlerts(props.initPriceAlertData.alerts);
    }
});

onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll);
    window.removeEventListener('resize', resizeHeader);
});
</script>

<style lang="scss">
.priceAlertsTable {
    .price-alerts-table {
        .price-alerts-name {
            min-width: 180px !important;
            width: 24%;
        }
        &.table {
            thead,
            tfoot {
                td,
                th {
                    @apply px-3 py-2;
                    font-size: .85rem;
                }
            }
            tbody {
                td,
                th {
                    @apply px-3;
                    font-size: .85rem;
                }
            }
            .sortable {
                @apply cursor-pointer;
            }
        }
    }
}
</style>
